import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LAMPORTS_PER_SOL, PublicKey, Transaction, TransactionMessage, VersionedTransaction } from '@solana/web3.js';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import {
    buildPlatformFeeShareTransactions,
    buildRemoveLiquidityTransactions,
    refreshLiquidityTransaction,
    getAddLiquidityQuote,
    getPool,
    getWalletPortfolio,
    getWalletPositions,
    logOrcaLiquidityEvent,
    prepareAddLiquidityTransactions
} from '../../services/OrcaService';
import { confirmTransactionRobust, connectionForTransactions, withTimeout } from '../../services/SolanaRpcService';
import { buildTxSuccessMessage, formatSolanaError } from '../../utils/solanaTxFeedback';
import './Orca.css';

const SOL_MINT = 'So11111111111111111111111111111111111111112';
const ORCA_MINT = 'orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE';
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
const SOLANA_RPC_URL = process.env.REACT_APP_SOLANA_RPC_URL;
const SEND_TRANSACTION_TIMEOUT_MS = 30_000;
const WALLET_SIGN_TIMEOUT_MS = 120_000;
const SOL_FEE_RESERVE = 0.02;
/** Rent aproximado: NFT da posição + ATAs + margem (openPosition). */
const SOL_MIN_NEW_POSITION = 0.035;
const SOL_MIN_EXISTING_POSITION = 0.012;
/** Limites de tick do Whirlpool (mesmos usados no preset Full). */
const WHIRLPOOL_TICK_LOWER_BOUND = -443636;
const WHIRLPOOL_TICK_UPPER_BOUND = 443636;
/** ln(1.0001): relação entre índice de tick e preço na aproximação usada em `tickToDisplayPrice`. */
const LN_TICK_RATIO = Math.log(1.0001);
const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
const TOKEN_2022_PROGRAM_ID = new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb');
const KNOWN_TOKENS = {
    [SOL_MINT]: { mint: SOL_MINT, symbol: 'SOL', name: 'Solana', decimals: 9 },
    [ORCA_MINT]: { mint: ORCA_MINT, symbol: 'ORCA', name: 'Orca', decimals: 6 },
    [USDC_MINT]: { mint: USDC_MINT, symbol: 'USDC', name: 'USD Coin', decimals: 6 }
};

/** BigInt no bundle browser (evita globalThis/self com no-undef / no-restricted-globals) */
const toBigInt = window.BigInt;

function isOrcaLiquidityDebugEnabled() {
    try {
        if (typeof window === 'undefined') return false;
        if (window.localStorage?.getItem('orcaLiquidityDebug') === '1') return true;
    } catch {
        /* ignore */
    }
    return process.env.REACT_APP_ORCA_LIQUIDITY_DEBUG === 'true';
}

/** Índice da instrução a partir de `sim.value.err` (formato Solana). */
function instructionIndexFromSimulationErr(errVal) {
    if (!errVal || typeof errVal !== 'object') return null;
    const ie = errVal.InstructionError;
    if (Array.isArray(ie) && ie.length >= 1 && typeof ie[0] === 'number') return ie[0];
    return null;
}

/** Tenta extrair N de mensagens tipo "Instruction 4 Failed" ou `{"InstructionError":[3,...]}`. */
function extractFailedInstructionIndex(err) {
    const s = `${err?.message || ''} ${err?.toString?.() || ''}`;
    // RPC / simulação: "Error processing Instruction 3: custom program error"
    const mColon = s.match(/Instruction\s+(\d+)\s*:/i);
    if (mColon) return Number(mColon[1]);
    const m1 = s.match(/Instruction\s+(\d+)\s+Failed/i);
    if (m1) return Number(m1[1]);
    // JSON.stringify(sim.value.err) → "InstructionError":[3,
    const mJson = s.match(/["']InstructionError["']\s*:\s*\[\s*(\d+)\s*,/);
    if (mJson) return Number(mJson[1]);
    const m2 = s.match(/InstructionError:\s*\[\s*(\d+)\s*,/i);
    if (m2) return Number(m2[1]);
    const m3 = s.match(/instruction\s*:\s*(\d+)/i);
    if (m3) return Number(m3[1]);
    return null;
}

function summarizeSignedTxDetail(signed) {
    try {
        let instructions;
        if (signed instanceof VersionedTransaction) {
            instructions = TransactionMessage.decompile(signed.message).instructions;
        } else {
            instructions = signed.compileMessage().instructions;
        }
        const instrucoes = instructions.map((ix, idx) => `${idx}:${ix.programId.toBase58()}`);
        const programaNa = i =>
            typeof i === 'number' && i >= 0 && i < instructions.length
                ? `${instructions[i].programId.toBase58()} keys=${instructions[i].keys?.length ?? 0}`
                : null;
        return { instrucoes, n: instructions.length, programaNa };
    } catch (e) {
        return { instrucoes: [`(nao foi possivel ler instrucoes: ${e.message})`], n: 0, programaNa: () => null };
    }
}

/** Lista índice → programa de cada instrução (útil quando o erro diz "Instruction 3"). */
function summarizeSignedTxInstructions(signed) {
    return summarizeSignedTxDetail(signed).instrucoes;
}

function depositDebugVerbose(label, data) {
    if (!isOrcaLiquidityDebugEnabled()) return;
    console.info('[OrcaLiquidity:debug]', label, data);
}

/** Avisos locais (antes de carregar no servidor); alinhados a `liquidityPrepareWarnings` no backend. */
function buildLiquidityDepositWarnings({ pool, tickLowerIndex, tickUpperIndex, positionAddress }) {
    const warnings = [];
    if (!pool) return warnings;

    const liqRaw = pool.liquidity;
    const liqZero = liqRaw == null || liqRaw === '0' || Number(liqRaw) === 0;
    if (liqZero && !positionAddress) {
        warnings.push(
            'Esta pool ainda não tem liquidez on-chain (primeiro depósito). O preço pode mover-se até confirmar; '
                + 'um slippage maior reduz rejeições por «preço moveu».'
        );
    }

    if (!positionAddress) {
        const lo = Number(tickLowerIndex);
        const hi = Number(tickUpperIndex);
        const tc = Number(pool.tickCurrentIndex);
        if ([lo, hi, tc].every(n => Number.isFinite(n)) && (tc < lo || tc >= hi)) {
            warnings.push(
                'O tick atual está fora do intervalo [lower, upper) — o depósito será maioritariamente num só token '
                    + 'e é mais sensível a movimentos de preço até a transação ser confirmada.'
            );
        }
    }

    return warnings;
}

function OrcaLiquidity() {
    const { poolAddress } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { connection } = useConnection();
    const { connected, publicKey, wallet, signTransaction } = useWallet();
    const [pool, setPool] = useState(null);
    const [balances, setBalances] = useState({ tokenA: 0, tokenB: 0 });
    const [walletAssets, setWalletAssets] = useState([]);
    const [position, setPosition] = useState(null);
    const [rangePreset, setRangePreset] = useState('medium');
    const [amountMode, setAmountMode] = useState('usd');
    const [autoSwap, setAutoSwap] = useState(true);
    const [form, setForm] = useState({
        amountA: '',
        amountB: '',
        totalUsd: '',
        removeUsd: '',
        slippageBps: '1500',
        tickLowerIndex: '',
        tickUpperIndex: ''
    });
    const [loading, setLoading] = useState('');
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [operationStatus, setOperationStatus] = useState('');
    const [balanceDebug, setBalanceDebug] = useState('');
    const [quoteInfo, setQuoteInfo] = useState(null);
    const [lastEditedSide, setLastEditedSide] = useState('');
    /** Preços na mesma base de `pool.display.price` (par exibido); convertidos em ticks ao aplicar. */
    const [priceRangeForm, setPriceRangeForm] = useState({ min: '', max: '' });
    const balanceRequestRef = useRef(0);
    const quoteRequestRef = useRef(0);
    const usdAlignRequestRef = useRef(0);
    const walletAddress = publicKey?.toBase58();
    const positionAddress = searchParams.get('position');
    const quoteInputAmount = lastEditedSide === 'A' ? form.amountA : form.amountB;

    useEffect(() => {
        if (amountMode !== 'usd') {
            usdAlignRequestRef.current += 1;
        }
    }, [amountMode]);

    useEffect(() => {
        loadPool();
    }, [poolAddress]);

    useEffect(() => {
        if (connected && publicKey && positionAddress) loadPosition();
        else setPosition(null);
    }, [connected, walletAddress, positionAddress]);

    useEffect(() => {
        if (!connected || !publicKey || !positionAddress) return undefined;
        const timer = setInterval(() => {
            refreshPositionData(true);
        }, 30000);
        return () => clearInterval(timer);
    }, [connected, walletAddress, positionAddress, poolAddress]);

    useEffect(() => {
        if (pool && connected && publicKey) {
            loadBalances(pool);
        } else {
            setBalances({ tokenA: 0, tokenB: 0 });
            setWalletAssets([]);
        }
    }, [pool, connected, walletAddress]);

    useEffect(() => {
        if (!pool || !lastEditedSide) return;
        if (!autoSwap) {
            setQuoteInfo(null);
            return;
        }
        if (!quoteInputAmount || Number(quoteInputAmount) <= 0) {
            setQuoteInfo(null);
            return;
        }
        const timer = setTimeout(() => refreshLiquidityQuote(lastEditedSide, quoteInputAmount), 450);
        return () => clearTimeout(timer);
    }, [pool, position?.positionAddress, lastEditedSide, quoteInputAmount, form.tickLowerIndex, form.tickUpperIndex, form.slippageBps, autoSwap]);

    useEffect(() => {
        if (!pool) return;
        const lo = form.tickLowerIndex;
        const hi = form.tickUpperIndex;
        if (lo === '' || lo === undefined || hi === '' || hi === undefined) return;
        if (!Number.isFinite(Number(lo)) || !Number.isFinite(Number(hi))) return;
        setPriceRangeForm(priceRangeFormStateFromTicks(pool, lo, hi));
    }, [pool, form.tickLowerIndex, form.tickUpperIndex]);

    useEffect(() => {
        if (amountMode !== 'usd' || !pool) return undefined;
        const trimmed = String(form.totalUsd || '').trim();
        const T = Number(trimmed.replace(',', '.'));
        if (!trimmed || !Number.isFinite(T) || T <= 0) {
            setQuoteInfo(null);
            return undefined;
        }
        const tickLowerIndex = form.tickLowerIndex;
        const tickUpperIndex = form.tickUpperIndex;
        const slippageBps = form.slippageBps;
        const positionAddr = position?.positionAddress;
        usdAlignRequestRef.current += 1;
        const requestId = usdAlignRequestRef.current;
        const timer = setTimeout(() => {
            alignTotalUsdWithQuote(T, { tickLowerIndex, tickUpperIndex, slippageBps, positionAddr }, requestId);
        }, 450);
        return () => clearTimeout(timer);
    }, [amountMode, form.totalUsd, form.tickLowerIndex, form.tickUpperIndex, form.slippageBps, pool, position?.positionAddress]);

    async function loadPool() {
        setLoading('pool');
        try {
            const data = await getPool(poolAddress, rpcOptions());
            setPool(data);
            const range = getRangeForPool(data, rangePreset);
            setForm(current => ({
                ...current,
                tickLowerIndex: range.lower,
                tickUpperIndex: range.upper,
                amountA: '',
                amountB: ''
            }));
        } catch (err) {
            showError(err);
        } finally {
            setLoading('');
        }
    }

    async function loadPosition() {
        await refreshPositionData(false);
    }

    async function refreshPositionData(silent = false) {
        if (!walletAddress || !positionAddress) return;
        if (!silent) setLoading('position');
        try {
            const [poolData, data] = await Promise.all([
                getPool(poolAddress, rpcOptions()),
                getWalletPositions(walletAddress, rpcOptions())
            ]);
            setPool(poolData);
            const found = (data.positions || []).find(item => item.positionAddress === positionAddress);
            setPosition(found || null);
            if (found) {
                setForm(current => ({
                    ...current,
                    tickLowerIndex: found.tickLowerIndex,
                    tickUpperIndex: found.tickUpperIndex
                }));
            }
        } catch (err) {
            if (!silent) showError(err);
            else logCompactError('position', err);
        } finally {
            if (!silent) setLoading('');
        }
    }

    async function loadBalances(poolData) {
        if (!connected || !publicKey || !walletAddress) {
            setNotification({ type: 'error', text: 'Conecte a carteira antes de atualizar o saldo.' });
            return;
        }
        const requestId = balanceRequestRef.current + 1;
        balanceRequestRef.current = requestId;
        setLoading('balances');
        try {
            const portfolio = await getWalletPortfolio(walletAddress, {
                ...rpcOptions(),
                mints: Object.keys(KNOWN_TOKENS)
            });
            if (requestId !== balanceRequestRef.current) return;
            let assets = normalizePortfolioAssets(portfolio.assets);
            if (!hasNonSolAssets(assets)) {
                assets = await loadWalletAssetsFromAlchemy();
            }
            if (requestId !== balanceRequestRef.current) return;
            setWalletAssets(assets);
            const nextBalances = balancesFromAssets(poolData, assets);
            setBalances(nextBalances);
            setBalanceDebug(formatBalanceDebug(assets));
        } catch (err) {
            logCompactError('saldo', err);
            const text = err.response?.data || err.message || 'Erro ao atualizar saldo.';
            setNotification({ type: 'error', text });
        } finally {
            if (requestId === balanceRequestRef.current) setLoading('');
        }
    }

    function onRefreshBalances() {
        if (!pool) return;
        loadBalances(pool);
    }

    function balancesFromAssets(poolData, assets) {
        return {
            tokenA: getBalanceForWalletToken(displayTokenFromPool(poolData, 'tokenA'), assets),
            tokenB: getBalanceForWalletToken(displayTokenFromPool(poolData, 'tokenB'), assets)
        };
    }

    function getBalanceFromPortfolio(mint, assets = []) {
        return assets
            .filter(asset => asset.mint === mint)
            .reduce((sum, asset) => sum + Number(asset.amount || 0), 0);
    }

    function getBalanceForWalletToken(token, assets = []) {
        const mint = normalizedTokenMint(token);
        const symbol = token?.symbol?.toUpperCase();
        return assets
            .filter(asset => {
                const assetSymbol = asset.symbol?.toUpperCase();
                return asset.mint === mint || (symbol && assetSymbol === symbol);
            })
            .reduce((sum, asset) => sum + Number(asset.amount || 0), 0);
    }

    function normalizePortfolioAssets(items = []) {
        return items.map(asset => ({
            ...(KNOWN_TOKENS[asset.mint] || {}),
            ...asset,
            symbol: asset.symbol || KNOWN_TOKENS[asset.mint]?.symbol,
            name: asset.name || KNOWN_TOKENS[asset.mint]?.name,
            amount: Number(asset.amount || 0)
        })).filter(asset => asset.amount > 0);
    }

    async function loadWalletAssetsFromAlchemy() {
        const rpc = transactionConnection();
        const [lamports, splAccounts, token2022Accounts] = await Promise.all([
            rpc.getBalance(publicKey),
            rpc.getParsedTokenAccountsByOwner(publicKey, { programId: TOKEN_PROGRAM_ID }),
            rpc.getParsedTokenAccountsByOwner(publicKey, { programId: TOKEN_2022_PROGRAM_ID }).catch(() => ({ value: [] }))
        ]);
        return normalizePortfolioAssets([
            {
                ...KNOWN_TOKENS[SOL_MINT],
                amount: lamports / LAMPORTS_PER_SOL
            },
            ...[...splAccounts.value, ...token2022Accounts.value].map(item => {
                const info = item.account.data.parsed.info;
                return {
                    ...(KNOWN_TOKENS[info.mint] || {}),
                    mint: info.mint,
                    amount: Number(info.tokenAmount.uiAmountString || info.tokenAmount.uiAmount || 0),
                    decimals: info.tokenAmount.decimals,
                    program: item.account.owner.toBase58()
                };
            })
        ]);
    }

    function hasNonSolAssets(assets = []) {
        return assets.some(asset => asset.mint !== SOL_MINT && Number(asset.amount || 0) > 0);
    }

    function formatBalanceDebug(assets = []) {
        if (!assets.length) return 'Nenhum ativo retornado pela rota de portfólio.';
        return `Ativos lidos: ${assets.map(asset => `${asset.symbol || shortAddress(asset.mint)} ${formatNumber(asset.amount, 6)}`).join(' · ')}`;
    }


    /** Evita encher o console com logs completos de simulação (milhares de caracteres). */
    function logCompactError(context, err) {
        const raw = String(err?.message || err || '').trim();
        const max = 420;
        const short = raw.length > max ? `${raw.slice(0, max)}…` : raw;
        const sig = err?.signature ? ` signature=${err.signature}` : '';
        console.warn(`[OrcaLiquidity${context ? `: ${context}` : ''}]${sig}`, short || '(sem mensagem)');
    }

    async function showError(err) {
        logCompactError('', err);
        let enriched = err;
        try {
            enriched = await enrichSendTransactionError(err, transactionConnection());
        } catch (_) {
            /* mantém err original */
        }
        setNotification({ type: 'error', text: formatSolanaError(enriched) });
    }

    function updateField(event) {
        const { name, value } = event.target;
        if (name === 'amountA') setLastEditedSide('A');
        if (name === 'amountB') setLastEditedSide('B');
        if (name === 'tickLowerIndex' || name === 'tickUpperIndex') {
            quoteRequestRef.current += 1;
            setQuoteInfo(null);
        }
        setForm(current => ({
            ...current,
            [name]: value
        }));
    }

    async function refreshLiquidityQuote(side, amount) {
        const requestId = quoteRequestRef.current + 1;
        quoteRequestRef.current = requestId;
        const inputToken = side === 'A' ? displayToken('tokenA') : displayToken('tokenB');
        try {
            const quote = await getAddLiquidityQuote({
                ...rpcOptions(),
                poolAddress: pool.address,
                positionAddress: position?.positionAddress,
                inputMint: normalizedTokenMint(inputToken),
                inputAmount: amount,
                tickLowerIndex: form.tickLowerIndex,
                tickUpperIndex: form.tickUpperIndex,
                slippageBps: form.slippageBps
            });
            if (requestId !== quoteRequestRef.current) return;
            setQuoteInfo(quote);
            setForm(current => {
                if (side === 'A') {
                    const amountB = displayAmountFromQuote(quote, 'B');
                    if (current.amountB === amountB) return current;
                    return {
                        ...current,
                        amountB
                    };
                }
                const amountA = displayAmountFromQuote(quote, 'A');
                if (current.amountA === amountA) return current;
                return {
                    ...current,
                    amountA
                };
            });
        } catch (err) {
            logCompactError('quote', err);
            setQuoteInfo(null);
        }
    }

    function displayAmountFromQuote(quote, side) {
        const token = side === 'A' ? displayToken('tokenA') : displayToken('tokenB');
        const rawTokenA = pool?.tokenA?.mint;
        const displayMint = normalizedTokenMint(token);
        const isDisplayTokenA = displayMint === rawTokenA || normalizedTokenMint(pool?.tokenA) === displayMint;
        const value = isDisplayTokenA ? quote.tokenEstA?.ui : quote.tokenEstB?.ui;
        return formatInputAmount(value);
    }

    function isOrcaPriceSlippageError(err) {
        const fromAxios = typeof err?.response?.data === 'string' ? err.response.data : '';
        let fromJson = '';
        try {
            fromJson = JSON.stringify(err?.err || err?.simulationResponse || err?.value || '');
        } catch {
            fromJson = '';
        }
        const msg = `${err?.message || ''} ${fromAxios} ${err?.cause?.message || ''} ${fromJson}`;
        // 0x17b5 = PriceSlippageOutOfBounds (Whirlpool); 6069 = decimal; 0x1788 = TokenMaxSubceeded / variações
        return /0x17b5|0x1788|6069|6024|PriceSlippageOutOfBounds|price outside slippage bounds|slippage tolerance|exceeded.*slippage|TokenMax|AmountMax|AmountMin/i.test(msg);
    }

    function spendableBalanceForToken(token) {
        const balance = balanceForToken(token);
        if (normalizedTokenMint(token) === SOL_MINT) {
            return Math.max(balance - SOL_FEE_RESERVE, 0);
        }
        return balance;
    }

    function spendableSolBalance() {
        const fromAssets = walletAssets
            .filter(a => a.mint === SOL_MINT)
            .reduce((s, a) => s + Number(a.amount || 0), 0);
        const raw = fromAssets > 0 ? fromAssets : balanceForToken({ mint: SOL_MINT, symbol: 'SOL' });
        return Math.max(raw - SOL_FEE_RESERVE, 0);
    }

    /** Mapeia tokenMax/Est on-chain → lados exibidos (display A/B). */
    function quoteLimitsForDisplay(quote) {
        if (!quote) {
            return { estA: 0, estB: 0, maxA: 0, maxB: 0 };
        }
        const inverted = Boolean(pool?.display?.inverted);
        if (!inverted) {
            return {
                estA: Number(quote.tokenEstA?.ui || 0),
                estB: Number(quote.tokenEstB?.ui || 0),
                maxA: Number(quote.tokenMaxA?.ui || 0),
                maxB: Number(quote.tokenMaxB?.ui || 0)
            };
        }
        return {
            estA: Number(quote.tokenEstB?.ui || 0),
            estB: Number(quote.tokenEstA?.ui || 0),
            maxA: Number(quote.tokenMaxB?.ui || 0),
            maxB: Number(quote.tokenMaxA?.ui || 0)
        };
    }

    function tokenMetaFromMint(mint) {
        if (!mint) return null;
        if (KNOWN_TOKENS[mint]) return KNOWN_TOKENS[mint];
        if (pool?.tokenA?.mint === mint) return pool.tokenA;
        if (pool?.tokenB?.mint === mint) return pool.tokenB;
        if (pool?.display?.tokenA?.mint === mint) return pool.display.tokenA;
        if (pool?.display?.tokenB?.mint === mint) return pool.display.tokenB;
        return { mint, symbol: shortAddress(mint) };
    }

    function spendableByMint(mint) {
        return spendableBalanceForToken(tokenMetaFromMint(mint));
    }

    /** Validação por mint on-chain (quote.tokenA/B), não pelo layout display. */
    function assertCanAffordLiquidityDeposit(prepared) {
        const quote = prepared?.quote;
        if (!quote?.tokenA?.mint || !quote?.tokenB?.mint || !pool) return;

        const shortfalls = [];
        const eps = 1e-9;
        const legs = [
            {
                mint: quote.tokenA.mint,
                est: Number(quote.tokenEstA?.ui || 0),
                max: Number(quote.tokenMaxA?.ui || 0)
            },
            {
                mint: quote.tokenB.mint,
                est: Number(quote.tokenEstB?.ui || 0),
                max: Number(quote.tokenMaxB?.ui || 0)
            }
        ];

        for (const { mint, est, max } of legs) {
            if (est <= eps && max <= eps) continue;
            const meta = tokenMetaFromMint(mint);
            const have = spendableByMint(mint);
            const need = est > eps ? est : max;
            if (have + eps < need) {
                shortfalls.push(
                    `${formatNumber(need, 6)} ${tokenLabel(meta)} (estimado pela pool; carteira ~${formatNumber(have, 6)})`
                );
            } else if (max > eps && have + eps < max) {
                shortfalls.push(
                    `${formatNumber(max, 6)} ${tokenLabel(meta)} no máximo com slippage (carteira ~${formatNumber(have, 6)})`
                );
            }
        }

        const minSol = position?.positionAddress ? SOL_MIN_EXISTING_POSITION : SOL_MIN_NEW_POSITION;
        const solHave = spendableSolBalance();
        if (solHave + eps < minSol) {
            shortfalls.push(
                `~${formatNumber(minSol, 3)} SOL para taxas/rent (disponível ~${formatNumber(solHave, 4)} SOL)`
            );
        }

        if (!shortfalls.length) return;

        const inRange = isCurrentTickInsideSelectedRange();
        const rangeHint = inRange
            ? ' O preço atual está dentro do range — a Orca exige os dois tokens do par, não só o valor digitado.'
            : '';

        throw new Error(
            `Saldo insuficiente para este depósito: ${shortfalls.join('; ')}.${rangeHint} `
                + 'Clique «Atualizar saldo» ou reduza o valor / alargue o range.'
        );
    }

    function formatDepositMaxHint(quote) {
        if (!quote?.tokenA?.mint || !quote?.tokenB?.mint) return '';
        const parts = [];
        const pushLeg = (mint, estUi, maxUi) => {
            const est = Number(estUi || 0);
            const max = Number(maxUi || 0);
            if (est <= 0 && max <= 0) return;
            const meta = tokenMetaFromMint(mint);
            if (max > 0 && Math.abs(max - est) > 1e-9) {
                parts.push(`até ${formatNumber(max, 6)} ${tokenLabel(meta)}`);
            } else if (est > 0) {
                parts.push(`~${formatNumber(est, 6)} ${tokenLabel(meta)}`);
            }
        };
        pushLeg(quote.tokenA.mint, quote.tokenEstA?.ui, quote.tokenMaxA?.ui);
        pushLeg(quote.tokenB.mint, quote.tokenEstB?.ui, quote.tokenMaxB?.ui);
        const minSol = position?.positionAddress ? SOL_MIN_EXISTING_POSITION : SOL_MIN_NEW_POSITION;
        const solLine = `+ ~${formatNumber(minSol, 3)} SOL (taxas/rent)`;
        const inRange = isCurrentTickInsideSelectedRange();
        const rangeNote = inRange ? ' Preço dentro do range: serão debitados os dois tokens do par. ' : ' ';
        if (!parts.length) return `Reserva na rede:${rangeNote}${solLine}.`;
        return `Depósito estimado na rede:${rangeNote}${parts.join(' + ')} ${solLine}.`;
    }

    function formatInputAmount(value) {
        const parsed = Number(value || 0);
        if (!Number.isFinite(parsed) || parsed <= 0) return '';
        return parsed.toFixed(8).replace(/\.?0+$/, '');
    }

    function onRangePresetChange(preset) {
        setRangePreset(preset);
        if (!pool) return;
        const range = getRangeForPool(pool, preset);
        quoteRequestRef.current += 1;
        setQuoteInfo(null);
        setForm(current => ({
            ...current,
            tickLowerIndex: range.lower,
            tickUpperIndex: range.upper
        }));
    }

    function onTotalUsdChange(event) {
        const totalUsd = event.target.value;
        const trimmed = String(totalUsd || '').trim();
        const T = Number(trimmed.replace(',', '.'));
        if (!trimmed || !Number.isFinite(T) || T <= 0) {
            setForm(current => ({
                ...current,
                totalUsd,
                amountA: '',
                amountB: ''
            }));
            setQuoteInfo(null);
            return;
        }
        setForm(current => ({ ...current, totalUsd }));
    }

    function onMaxAmount(side) {
        const token = displayToken(side === 'A' ? 'tokenA' : 'tokenB');
        const amount = spendableBalanceForToken(token);
        setLastEditedSide(side);
        setForm(current => ({
            ...current,
            [side === 'A' ? 'amountA' : 'amountB']: formatInputAmount(amount),
            totalUsd: ''
        }));
    }

    async function onAddLiquidity() {
        if (!connected || !publicKey) {
            setNotification({ type: 'error', text: 'Conecte a carteira antes de adicionar liquidez.' });
            return;
        }
        if (!signTransaction) {
            setNotification({ type: 'error', text: 'Sua carteira não expôs assinatura de transação.' });
            return;
        }
        if (!form.amountA && !form.amountB) {
            setNotification({ type: 'error', text: 'Informe ao menos um valor para depósito.' });
            return;
        }

        setLoading('add');
        setOperationStatus('Preparando depósito de liquidez...');
        let slippageBpsUsed = Math.min(10_000, Math.max(1, Number(form.slippageBps) || 1500));
        const maxSlippageAttempts = 12;
        const addLiquidityPreparePayload = () => ({
            ...rpcOptions(),
            walletPublicKey: publicKey.toBase58(),
            poolAddress: pool.address,
            positionAddress: position?.positionAddress,
            amountA: form.amountA,
            amountB: form.amountB,
            lastEditedSide,
            tickLowerIndex: form.tickLowerIndex,
            tickUpperIndex: form.tickUpperIndex,
            slippageBps: slippageBpsUsed,
            initTickArrays: !position
        });
        try {
            for (let attempt = 0; attempt < maxSlippageAttempts; attempt += 1) {
                try {
                    if (attempt > 0) {
                        setOperationStatus(`O preço moveu-se na rede; a retentar com slippage ${slippageBpsUsed} bps...`);
                    }
                    depositDebugVerbose('prepare request', {
                        attempt,
                        slippageBps: slippageBpsUsed,
                        pool: pool.address,
                        ticks: [form.tickLowerIndex, form.tickUpperIndex],
                        lastEditedSide,
                        amountA: form.amountA,
                        amountB: form.amountB,
                        positionAddress: position?.positionAddress || null
                    });
                    let prepared = await prepareAddLiquidityTransactions(addLiquidityPreparePayload());
                    /* Duplo prepare só para posição nova: última leitura da pool fica o mais perto possível da assinatura. */
                    if (!position?.positionAddress) {
                        prepared = await prepareAddLiquidityTransactions(addLiquidityPreparePayload());
                    }
                    depositDebugVerbose('prepare OK', {
                        n: prepared.transactions?.length,
                        tipos: prepared.transactions?.map(t => t.type),
                        selectedSide: prepared.selectedSide,
                        positionMint: prepared.positionMint,
                        liquidityAmount: prepared.quote?.liquidityAmount
                    });
                    setQuoteInfo(prepared.quote);
                    setForm(current => ({
                        ...current,
                        amountA: displayAmountFromQuote(prepared.quote, 'A') || current.amountA,
                        amountB: displayAmountFromQuote(prepared.quote, 'B') || current.amountB
                    }));
                    await loadBalances(pool);
                    assertCanAffordLiquidityDeposit(prepared);
                    let workingPrepared = prepared;
                    const rebuildPrepare = async () => prepareAddLiquidityTransactions(addLiquidityPreparePayload());
                    const { signatures, anyRecoveredAfterTimeout } = await signAndSendBuiltTransactions(
                        prepared.transactions,
                        'adicionar liquidez',
                        { rebuildPrepare }
                    );
                    setOperationStatus('Liquidez adicionada. Atualizando saldos...');
                    const retried = attempt > 0;
                    setNotification({
                        type: 'success',
                        text: buildTxSuccessMessage({
                            title: retried
                                ? `Liquidez adicionada (slippage efetivo ${slippageBpsUsed} bps após retentativa).`
                                : 'Liquidez adicionada com sucesso.',
                            signatures,
                            anyRecoveredAfterTimeout,
                            suffix: `Posição: ${workingPrepared.positionAddress}.`
                        })
                    });
                    if (positionAddress) await loadPosition();
                    await loadBalances(pool);
                    break;
                } catch (err) {
                    if (!isOrcaPriceSlippageError(err) || attempt === maxSlippageAttempts - 1) {
                        throw err;
                    }
                    slippageBpsUsed = Math.min(
                        10_000,
                        Math.max(slippageBpsUsed + 250, Math.round(slippageBpsUsed * 2.25))
                    );
                }
            }
        } catch (err) {
            showError(err);
        } finally {
            setLoading('');
            setOperationStatus('');
        }
    }

    function hasPendingPositionFees(pos = position) {
        if (!pos) return false;
        const a = Number(pos.pendingFees?.tokenA?.amount || 0);
        const b = Number(pos.pendingFees?.tokenB?.amount || 0);
        return a > 0 || b > 0;
    }

    function shouldApplyPlatformFee(platformFee, pos = position) {
        if (!hasPendingPositionFees(pos)) return false;
        if (!platformFee?.transfers?.length) return false;
        return platformFee.transfers.some(
            t => Number(t.owedAmount || 0) > 0 && Number(t.shareAmount || 0) > 0
        );
    }

    function liquidityLog(step, extra = {}) {
        logOrcaLiquidityEvent({
            step,
            positionAddress: position?.positionAddress,
            walletPublicKey: publicKey?.toBase58?.(),
            pendingFeeA: position?.pendingFees?.tokenA?.amount,
            pendingFeeB: position?.pendingFees?.tokenB?.amount,
            ...extra
        }).catch(() => {});
    }

    function isConfirmTimeoutError(err) {
        return /não foi possível confirmar a transação a tempo/i.test(String(err?.message || ''));
    }

    function extractSignatureFromError(err) {
        const msg = String(err?.message || err || '');
        const m = msg.match(/[1-9A-HJ-NP-Za-km-z]{64,88}/);
        return m ? m[0] : null;
    }

    function shouldAutoResumeRemoveOnly(err, { closePosition: closing = false } = {}) {
        const msg = String(err?.message || err?.response?.data || '').toLowerCase();
        if (isConfirmTimeoutError(err)) return true;
        if (closing && /closeposition|encerrar/i.test(msg)) return true;
        return /taxas já|already collected|fee.*owed.*0|nothing to collect|custom program error/i.test(msg);
    }

    async function transactionSucceededOnChain(rpc, signature) {
        if (!signature) return false;
        try {
            const tx = await rpc.getTransaction(signature, {
                commitment: 'confirmed',
                maxSupportedTransactionVersion: 0
            });
            if (tx && !tx.meta?.err) return true;
        } catch {
            /* ignore */
        }
        try {
            const { value } = await rpc.getSignatureStatuses([signature], { searchTransactionHistory: true });
            const status = value?.[0];
            if (status?.err) return false;
            return status?.confirmationStatus === 'confirmed'
                || status?.confirmationStatus === 'finalized'
                || (typeof status?.confirmations === 'number' && status.confirmations > 0);
        } catch {
            return false;
        }
    }

    function completeCloseSuccess(signatures, { lateConfirm = false, anyRecoveredAfterTimeout = false } = {}) {
        const closedAddress = position?.positionAddress;
        setOperationStatus('Posição encerrada. Atualizando dados...');
        setNotification({
            type: 'success',
            text: buildTxSuccessMessage({
                title: lateConfirm
                    ? 'Posição encerrada na rede (confirmação demorou; verifique no Explorer).'
                    : 'Posição encerrada com sucesso.',
                signatures,
                anyRecoveredAfterTimeout
            })
        });
        if (closedAddress) rememberClosedPosition(closedAddress);
        setPosition(null);
        navigate('/orca/positions', {
            state: {
                closedPosition: closedAddress,
                notice: 'Posição encerrada com sucesso. A lista foi atualizada.'
            }
        });
    }

    async function tryFinishCloseIfConfirmedOnChain(err, priorSignatures = []) {
        const sig = extractSignatureFromError(err);
        if (!sig) return false;
        const rpc = transactionConnection();
        if (!await transactionSucceededOnChain(rpc, sig)) return false;
        await refreshPositionData(true);
        if (position && Number(position.liquidity || 0) > 0) return false;
        liquidityLog('remove.user.close-confirmed-late', { signature: sig });
        completeCloseSuccess([...priorSignatures, sig], { lateConfirm: true, anyRecoveredAfterTimeout: true });
        return true;
    }

    async function runRemoveLiquidityFlow(closePosition, liquidityAmount, {
        feesAlreadyCollected = false,
        isAutoResume = false,
        skipCollectRewards = false
    } = {}) {
        liquidityLog('remove.flow.start', { closePosition, feesAlreadyCollected, isAutoResume, skipCollectRewards });
        if (!position?.positionAddress || position.positionAddress !== positionAddress) {
            await refreshPositionData(true);
        }
        if (!position?.positionAddress) {
            if (closePosition && isAutoResume) {
                liquidityLog('remove.flow.already-closed', {});
                completeCloseSuccess([], { lateConfirm: true });
                return;
            }
            liquidityLog('remove.flow.error', { message: 'position_not_found_after_refresh' });
            throw new Error('Posição não encontrada. Atualize a página ou abra a posição de novo.');
        }

        setOperationStatus('Montando transações no servidor...');
        const built = await withTimeout(
            buildRemoveLiquidityTransactions({
                ...rpcOptions(),
                walletPublicKey: publicKey.toBase58(),
                positionAddress: position.positionAddress,
                liquidityAmount: closePosition ? undefined : liquidityAmount,
                slippageBps: form.slippageBps,
                collectFees: true,
                collectRewards: true,
                closePosition,
                feesAlreadyCollected,
                skipCollectRewards
            }),
            90_000,
            'Tempo limite ao montar transações no servidor. Tente de novo.'
        );

        const onChainPending = built.onChainFees?.hasPending === true;
        const skipFeeSteps = feesAlreadyCollected || !onChainPending;
        liquidityLog('remove.flow.prepared', {
            closePosition,
            feesAlreadyCollected: skipFeeSteps,
            onChainPending,
            pendingFeeA: position.pendingFees?.tokenA?.amount,
            pendingFeeB: position.pendingFees?.tokenB?.amount
        });

        const platformFeeForSign = shouldApplyPlatformFee(built.platformFee) ? built.platformFee : null;

        liquidityLog('remove.flow.built', {
            closePosition,
            txTypes: (built.transactions || []).map(t => t.type),
            platformFeeTransfers: built.platformFee?.transfers?.length || 0,
            applyPlatformFee: Boolean(platformFeeForSign)
        });

        const { signatures, anyRecoveredAfterTimeout } = await signAndSendBuiltTransactions(
            built.transactions,
            closePosition ? 'encerrar posição' : 'sacar liquidez',
            {
                platformFee: platformFeeForSign,
                closePosition,
                rebuildContext: {
                    positionAddress: position.positionAddress,
                    slippageBps: form.slippageBps,
                    liquidityAmount: closePosition ? undefined : liquidityAmount
                }
            }
        );

        liquidityLog('remove.flow.ok', {
            closePosition,
            signatureCount: signatures.length,
            anyRecoveredAfterTimeout
        });

        if (closePosition) {
            completeCloseSuccess(signatures, { anyRecoveredAfterTimeout });
        } else {
            setOperationStatus('Saque concluído. Atualizando dados...');
            setNotification({
                type: 'success',
                text: buildTxSuccessMessage({
                    title: isAutoResume
                        ? 'Saque da liquidez concluído (taxas já estavam na carteira).'
                        : 'Saque concluído com sucesso.',
                    signatures,
                    anyRecoveredAfterTimeout
                })
            });
        }

        if (!closePosition) {
            await refreshPositionData(false);
            if (pool) await loadBalances(pool);
        }
    }

    async function onRemoveLiquidity(closePosition = false) {
        if (!position?.positionAddress) {
            setNotification({ type: 'error', text: 'Abra uma posição existente para sacar ou encerrar.' });
            return;
        }
        if (!connected || !publicKey || !signTransaction) {
            setNotification({ type: 'error', text: 'Conecte a carteira antes de gerenciar a posição.' });
            return;
        }

        setLoading(closePosition ? 'close' : 'remove');
        setOperationStatus(closePosition ? 'Preparando encerramento da posição...' : 'Preparando saque da posição...');
        liquidityLog('remove.user.start', { closePosition });
        let liquidityAmount;
        try {
            liquidityAmount = closePosition ? undefined : getRemoveLiquidityAmount();
            if (!closePosition && form.removeUsd && !liquidityAmount) {
                setNotification({ type: 'error', text: 'Informe um valor em USD válido para sacar.' });
                setLoading('');
                setOperationStatus('');
                return;
            }
            await runRemoveLiquidityFlow(closePosition, liquidityAmount, { feesAlreadyCollected: false });
        } catch (err) {
            liquidityLog('remove.user.error', {
                closePosition,
                message: String(err?.message || err).slice(0, 500)
            });
            if (closePosition && await tryFinishCloseIfConfirmedOnChain(err)) {
                return;
            }
            await refreshPositionData(true);
            const canResume = shouldAutoResumeRemoveOnly(err, { closePosition })
                || (closePosition && isConfirmTimeoutError(err))
                || !hasPendingPositionFees(position);
            if (canResume) {
                try {
                    liquidityLog('remove.user.auto-resume', {
                        closePosition,
                        skipCollectRewards: closePosition
                    });
                    setOperationStatus(
                        closePosition
                            ? 'A confirmação demorou; a tentar só encerrar a posição...'
                            : 'Taxas já coletadas na rede. Concluindo saque da posição...'
                    );
                    setNotification({
                        type: 'info',
                        text: closePosition
                            ? 'A rede demorou a confirmar. O app vai tentar apenas encerrar a posição (sem repetir recompensas).'
                            : 'As taxas já estão na carteira. O app vai continuar só com o saque.'
                    });
                    await runRemoveLiquidityFlow(closePosition, liquidityAmount, {
                        feesAlreadyCollected: true,
                        isAutoResume: true,
                        skipCollectRewards: Boolean(closePosition)
                    });
                    return;
                } catch (retryErr) {
                    if (closePosition && await tryFinishCloseIfConfirmedOnChain(retryErr)) {
                        return;
                    }
                    showError(retryErr);
                    return;
                }
            }
            showError(err);
        } finally {
            setLoading('');
            setOperationStatus('');
        }
    }

    function transactionStepLabel(type) {
        const labels = {
            decreaseLiquidity: 'sacar liquidez',
            collectFees: 'coletar taxas',
            platformFeeShare: 'taxa da plataforma (1%)',
            collectRewards: 'coletar recompensas',
            closePosition: 'encerrar posição'
        };
        return labels[type] || 'transação';
    }

    function rememberClosedPosition(address) {
        if (!address) return;
        try {
            const key = closedPositionsStorageKey(walletAddress);
            const current = JSON.parse(sessionStorage.getItem(key) || '[]');
            sessionStorage.setItem(key, JSON.stringify([...new Set([...current, address])]));
        } catch (err) {
            console.error(err);
        }
    }

    function closedPositionsStorageKey(address) {
        return `orcaClosedPositions:${address || 'unknown'}`;
    }

    function getRemoveLiquidityAmount() {
        if (!position) return undefined;
        if (!form.removeUsd) return position.liquidity;
        const positionUsd = getPositionUsdValue();
        const desiredUsd = Math.min(Number(form.removeUsd || 0), positionUsd);
        if (!positionUsd || !desiredUsd) return undefined;

        const scale = 1_000_000_000;
        const ratioScaled = toBigInt(Math.max(1, Math.floor((desiredUsd / positionUsd) * scale)));
        const liquidity = toBigInt(position.liquidity || '0');
        const amount = liquidity * ratioScaled / toBigInt(scale);
        return amount > toBigInt(0) ? amount.toString() : undefined;
    }

    function getPositionUsdValue() {
        if (!position) return 0;
        const amountA = Number(position.tokenAmounts?.tokenA?.amount || 0);
        const amountB = Number(position.tokenAmounts?.tokenB?.amount || 0);
        return (amountA * getTokenUsdPriceForToken(position.tokenAmounts?.tokenA))
            + (amountB * getTokenUsdPriceForToken(position.tokenAmounts?.tokenB));
    }

    function getPositionPendingFeesUsdValue() {
        if (!position) return 0;
        const feeA = Number(position.pendingFees?.tokenA?.amount || 0);
        const feeB = Number(position.pendingFees?.tokenB?.amount || 0);
        return (feeA * getTokenUsdPriceForToken(position.pendingFees?.tokenA))
            + (feeB * getTokenUsdPriceForToken(position.pendingFees?.tokenB));
    }

    function getPositionCollectedFeesUsdValue() {
        if (!position) return 0;
        const feeA = Number(position.collectedFees?.tokenA?.amount || 0);
        const feeB = Number(position.collectedFees?.tokenB?.amount || 0);
        return (feeA * getTokenUsdPriceForToken(position.collectedFees?.tokenA))
            + (feeB * getTokenUsdPriceForToken(position.collectedFees?.tokenB));
    }

    function getPositionFeeShare() {
        if (!position?.inRange) return 0;
        const positionLiquidity = Number(position.liquidity || 0);
        const activePoolLiquidity = Number(pool?.liquidity || 0);
        if (!positionLiquidity || !activePoolLiquidity) return 0;
        return positionLiquidity / activePoolLiquidity;
    }

    function getPositionFeeEstimatesByPeriod() {
        const share = getPositionFeeShare();
        const periods = [
            { key: '1h', label: '1h', fees: Number(pool?.fees1h || 0) },
            { key: '4h', label: '4h', fees: Number(pool?.fees4h || 0) },
            { key: '24h', label: '24h', fees: Number(pool?.fees24h || 0) },
            { key: '7d', label: '7d', fees: Number(pool?.fees7d || 0) }
        ];
        return periods.map(period => ({
            ...period,
            amountUsd: share && period.fees ? period.fees * share : 0
        }));
    }

    function getRemoveRatio() {
        const positionUsd = getPositionUsdValue();
        if (!positionUsd || !form.removeUsd) return 1;
        return Math.min(Number(form.removeUsd || 0) / positionUsd, 1);
    }

    function getEstimatedWithdrawAmounts() {
        const ratio = getRemoveRatio();
        return {
            tokenA: Number(position?.tokenAmounts?.tokenA?.amount || 0) * ratio,
            tokenB: Number(position?.tokenAmounts?.tokenB?.amount || 0) * ratio
        };
    }

    function getRangeForPool(poolData, preset) {
        const currentTick = Number(poolData?.tickCurrentIndex || 0);
        const spacing = Number(poolData?.tickSpacing || 1);
        const widths = { narrow: 20, medium: 80, wide: 220, full: 443636 };
        if (preset === 'full') {
            return {
                lower: Math.ceil(-443636 / spacing) * spacing,
                upper: Math.floor(443636 / spacing) * spacing
            };
        }
        const width = (widths[preset] || widths.medium) * spacing;
        return {
            lower: Math.floor((currentTick - width) / spacing) * spacing,
            upper: Math.ceil((currentTick + width) / spacing) * spacing
        };
    }

    function rpcOptions() {
        const url = SOLANA_RPC_URL || connection.rpcEndpoint;
        return url ? { rpcUrl: url, commitment: 'confirmed' } : { commitment: 'confirmed' };
    }

    function transactionConnection() {
        const endpoint = SOLANA_RPC_URL || connection.rpcEndpoint;
        return connectionForTransactions(endpoint);
    }

    function shouldRebuildLiquidityTransaction(err) {
        const msg = String(err?.message || err || '').toLowerCase();
        return msg.includes('signature verification')
            || msg.includes('blockhash not found')
            || msg.includes('block height exceeded')
            || msg.includes('transaction expired')
            || msg.includes('expir');
    }

    async function signAndSendBuiltTransactions(transactions = [], actionLabel, options = {}) {
        const { resyncLiquidityPrepare, rebuildPrepare, platformFee, closePosition, rebuildContext } = options;
        const queue = [...transactions];
        if (!queue.length) throw new Error('Nenhuma transação foi preparada para esta operação.');

        const signatures = [];
        let anyRecoveredAfterTimeout = false;
        const applyPlatformFee = shouldApplyPlatformFee(platformFee);
        const txTotalEstimate = queue.length + (applyPlatformFee ? (platformFee?.transfers?.length || 0) : 0);

        liquidityLog('sign.queue.start', {
            closePosition,
            txTypes: queue.map(t => t.type),
            applyPlatformFee,
            txTotalEstimate
        });

        async function fetchFreshLiquidityTxItem(item, reason) {
            const prepareFn = rebuildPrepare || resyncLiquidityPrepare;
            const isLiquidityTx = item.type === 'openPosition'
                || item.type === 'increaseLiquidity'
                || item.type === 'initTickArrays';
            if (!isLiquidityTx || typeof prepareFn !== 'function') {
                return item;
            }
            setOperationStatus(
                'A sincronizar preço da pool e sqrt bounds (min/max) imediatamente antes de assinar...'
            );
            liquidityLog('sign.tx.prepare-fresh', { txType: item.type, reason });
            const fresh = await prepareFn();
            const match = (fresh?.transactions || []).find(t => t.type === item.type);
            if (!match?.transaction) {
                throw new Error(
                    `Não foi possível remontar «${item.type}» com o preço atual da pool (${reason}). `
                        + 'Tente de novo ou aumente o slippage (bps).'
                );
            }
            depositDebugVerbose('tx fresca antes de assinar', {
                txType: item.type,
                reason,
                liquidityInputDebug: fresh.liquidityInputDebug
            });
            return match;
        }

        async function signAndSendOne(item, stepLabel, txIndex) {
            liquidityLog('sign.tx.start', {
                closePosition,
                txType: item.type,
                txIndex,
                txTotal: txTotalEstimate
            });

            let txItem = await fetchFreshLiquidityTxItem(item, 'pre-sign');
            let signed;
            const maxRebuildAttempts = 3;
            for (let rebuildAttempt = 0; rebuildAttempt < maxRebuildAttempts; rebuildAttempt += 1) {
                const tx = deserializeBuiltTransaction(txItem);
                depositDebugVerbose('assinar e enviar', {
                    passo: stepLabel,
                    tipo: item.type,
                    accao: actionLabel,
                    rebuildAttempt
                });
                setOperationStatus(`Abra a carteira e aprove: ${stepLabel}`);
                signed = await withTimeout(
                    signTransaction(tx),
                    WALLET_SIGN_TIMEOUT_MS,
                    `A carteira não respondeu em ${WALLET_SIGN_TIMEOUT_MS / 1000}s (${stepLabel}). `
                        + 'Verifique se a extensão está aberta, desbloqueada e na rede Solana mainnet.'
                );
                setOperationStatus(`Enviando (${stepLabel})...`);
                try {
                    const { signature, recoveredAfterTimeout } = await sendSignedTransaction(
                        signed,
                        (signatureValue, elapsedSeconds = 0) => {
                            setOperationStatus(
                                `Confirmando na rede (${stepLabel}) — a próxima assinatura na carteira só depois. `
                                + `${shortAddress(signatureValue)}`
                                + (elapsedSeconds ? ` · ${elapsedSeconds}s` : '')
                            );
                        },
                        {
                            actionLabel,
                            stepType: item.type,
                            stepIndex: signatures.length,
                            stepTotal: queue.length
                        }
                    );
                    if (recoveredAfterTimeout) anyRecoveredAfterTimeout = true;
                    signatures.push(signature);
                    liquidityLog('sign.tx.ok', {
                        closePosition,
                        txType: item.type,
                        txIndex,
                        signature,
                        recoveredAfterTimeout,
                        rebuilt: rebuildAttempt > 0
                    });
                    setOperationStatus(`${actionLabel}: ${stepLabel} confirmado.`);
                    return;
                } catch (sendErr) {
                    const isPrice = isOrcaPriceSlippageError(sendErr);
                    const canPrepareAgain = (rebuildPrepare || resyncLiquidityPrepare)
                        && (item.type === 'openPosition' || item.type === 'increaseLiquidity' || item.type === 'initTickArrays');
                    if (isPrice && canPrepareAgain && rebuildAttempt < maxRebuildAttempts - 1) {
                        liquidityLog('sign.tx.retry-price', { txType: item.type, attempt: rebuildAttempt });
                        setOperationStatus(`Preço moveu na rede; a remontar (${stepLabel})...`);
                        txItem = await fetchFreshLiquidityTxItem(item, 'price-slippage-retry');
                        continue;
                    }
                    const canRebuild = rebuildContext
                        && item.type !== 'platformFeeShare'
                        && ['collectFees', 'collectRewards', 'closePosition', 'decreaseLiquidity'].includes(item.type);
                    if (rebuildAttempt < maxRebuildAttempts - 1 && canRebuild && shouldRebuildLiquidityTransaction(sendErr)) {
                        liquidityLog('sign.tx.rebuild', { closePosition, txType: item.type });
                        setOperationStatus(`Remontando transação (${stepLabel}) com blockhash atual...`);
                        txItem = await refreshLiquidityTransaction({
                            ...rpcOptions(),
                            walletPublicKey: publicKey.toBase58(),
                            ...rebuildContext,
                            txType: item.type
                        });
                        continue;
                    }
                    throw sendErr;
                }
            }
        }

        async function signPlatformFeeTransfers(afterIndex) {
            if (!applyPlatformFee || !publicKey) return;

            setOperationStatus('Preparando taxa da plataforma (1%) na API — ainda não é assinatura na carteira...');
            liquidityLog('sign.platform-fee.prepare', { closePosition });
            const feeBuilt = await withTimeout(
                buildPlatformFeeShareTransactions({
                    ...rpcOptions(),
                    walletPublicKey: publicKey.toBase58(),
                    platformFee
                }),
                60_000,
                'Tempo limite ao preparar transações da taxa da plataforma (1%).'
            );
            const feeTxs = feeBuilt.transactions || [];
            liquidityLog('sign.platform-fee.built', { closePosition, txCount: feeTxs.length });
            for (let fi = 0; fi < feeTxs.length; fi += 1) {
                const feeItem = feeTxs[fi];
                const feeLabel = transactionStepLabel(feeItem.type);
                await signAndSendOne(feeItem, feeLabel, afterIndex + fi + 1);
            }
        }

        const hasCollectStep = queue.some(t => t.type === 'collectFees');
        if (applyPlatformFee && !hasCollectStep) {
            await signPlatformFeeTransfers(0);
        }

        for (let index = 0; index < queue.length; index += 1) {
            let item = queue[index];
            const liquidityStep = item.type === 'openPosition' || item.type === 'increaseLiquidity';

            const stepLabel = transactionStepLabel(item.type);
            try {
                await signAndSendOne(item, stepLabel, index + 1);
            } catch (stepErr) {
                if (item.type !== 'collectFees') {
                    liquidityLog('sign.tx.error', {
                        closePosition,
                        txType: item.type,
                        message: String(stepErr?.message || stepErr).slice(0, 500)
                    });
                    throw stepErr;
                }
                const lastSig = signatures[signatures.length - 1];
                const rpc = transactionConnection();
                if (lastSig && await transactionSucceededOnChain(rpc, lastSig)) {
                    setOperationStatus('Taxas confirmadas na rede; continuando...');
                    liquidityLog('sign.collect-fees.recovered', { closePosition, signature: lastSig });
                } else if (!isConfirmTimeoutError(stepErr) && !shouldAutoResumeRemoveOnly(stepErr)) {
                    liquidityLog('sign.collect-fees.error', {
                        closePosition,
                        message: String(stepErr?.message || stepErr).slice(0, 500)
                    });
                    throw stepErr;
                } else {
                    setOperationStatus('Coleta de taxas pode já ter sido feita; continuando...');
                    liquidityLog('sign.collect-fees.skip-continue', { closePosition });
                }
            }

            if (item.type === 'collectFees' && applyPlatformFee) {
                try {
                    await signPlatformFeeTransfers(index);
                } catch (feeErr) {
                    if (!shouldAutoResumeRemoveOnly(feeErr)) {
                        liquidityLog('sign.platform-fee.error', {
                            closePosition,
                            message: String(feeErr?.message || feeErr).slice(0, 500)
                        });
                        throw feeErr;
                    }
                    setOperationStatus('Taxa da plataforma ignorada; continuando saque...');
                    liquidityLog('sign.platform-fee.skip-continue', { closePosition });
                }
            }
        }

        liquidityLog('sign.queue.done', { closePosition, signatureCount: signatures.length });
        return { signatures, anyRecoveredAfterTimeout };
    }

    function deserializeBuiltTransaction(item) {
        const bytes = Uint8Array.from(atob(item.transaction), c => c.charCodeAt(0));
        return item.versioned ? VersionedTransaction.deserialize(bytes) : Transaction.from(bytes);
    }

    function serializeTransactionBase64(tx) {
        const bytes = tx.serialize({ requireAllSignatures: false });
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    async function refreshBuiltTransactionBlockhash(item) {
        const rpc = transactionConnection();
        const { blockhash, lastValidBlockHeight } = await rpc.getLatestBlockhash('confirmed');
        const tx = deserializeBuiltTransaction(item);

        if (tx instanceof VersionedTransaction) {
            const decompiled = TransactionMessage.decompile(tx.message);
            const refreshed = new TransactionMessage({
                payerKey: decompiled.payerKey,
                recentBlockhash: blockhash,
                instructions: decompiled.instructions
            }).compileToV0Message(decompiled.addressTableLookups);
            const out = new VersionedTransaction(refreshed);
            return {
                ...item,
                transaction: serializeTransactionBase64(out),
                recentBlockhash: blockhash,
                lastValidBlockHeight
            };
        }

        tx.recentBlockhash = blockhash;
        if (lastValidBlockHeight != null) tx.lastValidBlockHeight = lastValidBlockHeight;
        return {
            ...item,
            transaction: serializeTransactionBase64(tx),
            versioned: false,
            recentBlockhash: blockhash,
            lastValidBlockHeight
        };
    }

    async function enrichSendTransactionError(err, rpc) {
        if (!err || typeof err.getLogs !== 'function' || !rpc) return err;
        try {
            const logs = await err.getLogs(rpc);
            if (!Array.isArray(logs) || !logs.length) return err;
            const tail = logs.slice(-25).join(' | ');
            return new Error(`${err.message || err}\nLogs (Solana): ${tail}`);
        } catch {
            return err;
        }
    }

    async function sendSignedTransaction(signed, onSignature, meta = {}) {
        const rpc = transactionConnection();
        let signature;
        const liquidityStep = meta.stepType === 'openPosition' || meta.stepType === 'increaseLiquidity';
        try {
            if (liquidityStep) {
                const sim = await rpc.simulateTransaction(signed, {
                    commitment: 'processed',
                    sigVerify: false
                });
                if (sim.value.err) {
                    const blob = `${JSON.stringify(sim.value.err)}\n${(sim.value.logs || []).join('\n')}`;
                    depositDebugVerbose('simulacao liquidez falhou', { err: sim.value.err, logsTail: sim.value.logs?.slice?.(-20) });
                    if (isOrcaPriceSlippageError({ message: blob })) {
                        const ixSim = instructionIndexFromSimulationErr(sim.value.err);
                        const ixPart = ixSim != null ? ` ${ixSim}` : '';
                        throw new Error(
                            `Transaction simulation failed: Error processing Instruction${ixPart}: custom program error: 0x17b5`
                            + `\n${blob.slice(0, 900)}`
                        );
                    }
                    throw new Error(`Transaction simulation failed: ${blob.slice(0, 900)}`);
                }
                depositDebugVerbose('simulacao liquidez OK', { unitsConsumed: sim.value.unitsConsumed });
            } else if (isOrcaLiquidityDebugEnabled()) {
                try {
                    const sim = await rpc.simulateTransaction(signed, {
                        commitment: 'processed',
                        sigVerify: false
                    });
                    if (sim.value.err) {
                        console.warn('[OrcaLiquidity:simulacao pre-envio]', {
                            err: sim.value.err,
                            logsTail: sim.value.logs?.slice?.(-30)
                        });
                    } else {
                        depositDebugVerbose('simulacao pre-envio OK', {
                            unitsConsumed: sim.value.unitsConsumed
                        });
                    }
                } catch (simErr) {
                    depositDebugVerbose('simulacao pre-envio falhou (ignorado)', String(simErr?.message || simErr));
                }
            }
            const skipPreflight = true;
            signature = await withTimeout(
                rpc.sendRawTransaction(signed.serialize(), {
                    skipPreflight,
                    maxRetries: 3
                }),
                SEND_TRANSACTION_TIMEOUT_MS,
                'Tempo limite ao enviar a transação para a rede Solana. Tente novamente ou troque o RPC.'
            );
        } catch (err) {
            const detail = summarizeSignedTxDetail(signed);
            const ixIdx = extractFailedInstructionIndex(err);
            console.info('[OrcaLiquidity:falha no envio]', {
                accao: meta.actionLabel,
                passo: meta.stepIndex != null && meta.stepTotal != null
                    ? `${meta.stepIndex + 1}/${meta.stepTotal}`
                    : undefined,
                tipoPasso: meta.stepType,
                totalInstrucoes: detail.n,
                instrucoes: detail.instrucoes,
                ixFalhou: ixIdx,
                programaNaIxFalha: ixIdx != null ? detail.programaNa(ixIdx) : null,
                erroResumo: String(err?.message || err).slice(0, 400)
            });
            throw await enrichSendTransactionError(err, rpc);
        }
        if (onSignature) onSignature(signature);
        try {
            const { recoveredAfterTimeout } = await confirmTransactionRobust(
                rpc,
                signature,
                elapsedSeconds => {
                    if (onSignature) {
                        onSignature(signature, elapsedSeconds);
                    }
                }
            );
            return { signature, recoveredAfterTimeout };
        } catch (confirmErr) {
            if (await transactionSucceededOnChain(rpc, signature)) {
                return { signature, recoveredAfterTimeout: true };
            }
            throw confirmErr;
        }
    }

    function delay(ms) {
        return new Promise(resolve => {
            setTimeout(resolve, ms);
        });
    }

    function getTokenUsdPrice(side) {
        const token = side === 'A' ? displayToken('tokenA') : displayToken('tokenB');
        return getTokenUsdPriceForToken(token);
    }

    function getTokenUsdPriceForToken(token) {
        if (!token) return 0;
        const prices = getPoolDerivedUsdPrices();
        if (tokensMatch(token, pool?.tokenA)) return prices.tokenA;
        if (tokensMatch(token, pool?.tokenB)) return prices.tokenB;
        return 0;
    }

    /** Valor em USD da estimativa da quote (montantes na ordem on-chain A/B da pool). */
    function quoteDepositValueUsd(quote) {
        if (!quote || !pool) return 0;
        const pa = getTokenUsdPriceForToken(pool.tokenA);
        const pb = getTokenUsdPriceForToken(pool.tokenB);
        const a = Number(quote.tokenEstA?.ui ?? 0);
        const b = Number(quote.tokenEstB?.ui ?? 0);
        if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
        return a * pa + b * pb;
    }

    /**
     * Ajusta montantes A/B para ~totalUsd usando várias quotes da pool (curva + range reais).
     * Evita a fórmula em spot (mistura ordem chain/display e USD) que gerava ex. ~1 SOL por 3 USD.
     */
    async function alignTotalUsdWithQuote(T, { tickLowerIndex, tickUpperIndex, slippageBps, positionAddr }, requestId) {
        if (!pool || requestId !== usdAlignRequestRef.current) return;
        const displayA = displayToken('tokenA');
        const usdDispA = getTokenUsdPrice('A');
        if (!usdDispA || !Number.isFinite(usdDispA) || usdDispA <= 0) {
            setNotification({
                type: 'error',
                text: 'Não foi possível estimar USD do 1.º token para o total em dólar; verifique a pool.'
            });
            return;
        }
        const maxIn = spendableBalanceForToken(displayA);
        let amountIn = T / (2 * usdDispA);
        if (Number.isFinite(maxIn) && maxIn > 0) {
            amountIn = Math.min(amountIn, maxIn * 0.999);
        }
        amountIn = Math.max(amountIn, 1e-12);
        const inputMint = normalizedTokenMint(displayA);
        let lastQuote = null;
        try {
            for (let i = 0; i < 8; i += 1) {
                if (requestId !== usdAlignRequestRef.current) return;
                const quote = await getAddLiquidityQuote({
                    ...rpcOptions(),
                    poolAddress: pool.address,
                    positionAddress: positionAddr,
                    inputMint,
                    inputAmount: String(amountIn),
                    tickLowerIndex,
                    tickUpperIndex,
                    slippageBps
                });
                lastQuote = quote;
                const V = quoteDepositValueUsd(quote);
                if (!V || !Number.isFinite(V) || V <= 0) break;
                const ratio = T / V;
                if (Math.abs(ratio - 1) <= 0.04) break;
                amountIn *= ratio;
                if (Number.isFinite(maxIn) && maxIn > 0) {
                    amountIn = Math.min(amountIn, maxIn * 0.999);
                }
                if (!Number.isFinite(amountIn) || amountIn <= 0) break;
            }
        } catch (err) {
            logCompactError('usd-align', err);
            const api = typeof err?.response?.data === 'string' ? err.response.data : '';
            setNotification({
                type: 'error',
                text: api || err?.message || 'Erro ao alinhar o total em USD com a pool.'
            });
            return;
        }
        if (requestId !== usdAlignRequestRef.current || !lastQuote) return;
        setQuoteInfo(lastQuote);
        setLastEditedSide('A');
        setForm(current => ({
            ...current,
            amountA: displayAmountFromQuote(lastQuote, 'A'),
            amountB: displayAmountFromQuote(lastQuote, 'B')
        }));
    }

    function getPoolDerivedUsdPrices() {
        if (!pool) return { tokenA: 0, tokenB: 0 };
        const rawPrice = Number(pool.price || 0);
        const tokenA = pool.tokenA;
        const tokenB = pool.tokenB;
        if (!Number.isFinite(rawPrice) || rawPrice <= 0) return { tokenA: 0, tokenB: 0 };

        if (isUsdStableToken(tokenB)) return { tokenA: rawPrice, tokenB: 1 };
        if (isUsdStableToken(tokenA)) return { tokenA: 1, tokenB: rawPrice ? 1 / rawPrice : 0 };

        const tvl = Number(pool.tvlUsdc || 0);
        const reserveA = poolReserveUi('A');
        const reserveB = poolReserveUi('B');
        const denominator = (reserveA * rawPrice) + reserveB;
        if (!tvl || !reserveA || !reserveB || !denominator) return { tokenA: 0, tokenB: 0 };

        const tokenBUsd = tvl / denominator;
        return {
            tokenA: rawPrice * tokenBUsd,
            tokenB: tokenBUsd
        };
    }

    function poolReserveUi(side) {
        const token = side === 'A' ? pool?.tokenA : pool?.tokenB;
        const rawValue = Number(side === 'A' ? pool?.tokenBalanceA : pool?.tokenBalanceB);
        const decimals = Number(token?.decimals || 0);
        if (!Number.isFinite(rawValue) || rawValue <= 0) return 0;
        return rawValue / Math.pow(10, decimals);
    }

    function isUsdStableToken(token) {
        const symbol = token?.symbol?.toUpperCase();
        return symbol === 'USDC' || symbol === 'USDT' || symbol === 'USDH' || symbol === 'PYUSD';
    }

    /** Preço na UI (token B por token A, respeitando `display.inverted`) para um tick, dado o estado da pool. */
    function tickToDisplayPriceForPool(poolData, tick) {
        if (!poolData || tick === '' || tick === undefined || tick === null) return null;
        const currentTick = Number(poolData.tickCurrentIndex || 0);
        const currentPrice = Number(poolData.display?.price || poolData.price || 0);
        if (!Number.isFinite(currentPrice) || currentPrice <= 0) return null;
        const exponent = poolData.display?.inverted ? currentTick - Number(tick) : Number(tick) - currentTick;
        return currentPrice * Math.pow(1.0001, exponent);
    }

    function priceRangeFormStateFromTicks(poolData, tickLo, tickHi) {
        const lo = tickToDisplayPriceForPool(poolData, tickLo);
        const hi = tickToDisplayPriceForPool(poolData, tickHi);
        if (lo == null || hi == null) return { min: '', max: '' };
        return {
            min: String(Math.min(lo, hi)),
            max: String(Math.max(lo, hi))
        };
    }

    function tickToRelativePrice(tick) {
        if (!pool || tick === '' || tick === undefined || tick === null) return '-';
        const price = tickToDisplayPrice(tick);
        return price ? formatNumber(price, 8) : '-';
    }

    function tickToDisplayPrice(tick) {
        return tickToDisplayPriceForPool(pool, tick);
    }

    function parseLocaleDecimal(raw) {
        if (raw === '' || raw === undefined || raw === null) return NaN;
        const normalized = String(raw).trim().replace(/\s/g, '').replace(',', '.');
        return Number(normalized);
    }

    /**
     * Inverte `tickToDisplayPrice`: converte preço na base exibida (token B por token A, ou invertido)
     * em índice de tick contínuo, antes do alinhamento ao tickSpacing.
     */
    function displayPriceToRawTick(displayPrice) {
        if (!pool) return NaN;
        const currentTick = Number(pool.tickCurrentIndex || 0);
        const currentPrice = Number(pool.display?.price || pool.price || 0);
        const p = typeof displayPrice === 'number' ? displayPrice : parseLocaleDecimal(displayPrice);
        if (!Number.isFinite(currentTick) || !Number.isFinite(p) || p <= 0) return NaN;
        if (!Number.isFinite(currentPrice) || currentPrice <= 0) return NaN;
        const ratio = p / currentPrice;
        if (ratio <= 0) return NaN;
        const delta = Math.log(ratio) / LN_TICK_RATIO;
        return pool.display?.inverted ? currentTick - delta : currentTick + delta;
    }

    function alignTickToSpacing(tick, spacing, mode) {
        const s = Number(spacing) || 1;
        const t = Number(tick);
        if (!Number.isFinite(t) || !Number.isFinite(s) || s <= 0) return 0;
        if (mode === 'floor') return Math.floor(t / s) * s;
        return Math.ceil(t / s) * s;
    }

    function applyDisplayPricesToTicks() {
        if (!pool) return;
        const minP = parseLocaleDecimal(priceRangeForm.min);
        const maxP = parseLocaleDecimal(priceRangeForm.max);
        if (!Number.isFinite(minP) || !Number.isFinite(maxP) || minP <= 0 || maxP <= 0) {
            setNotification({
                type: 'error',
                text: 'Informe preço mínimo e máximo válidos (maiores que zero), na mesma base do preço mostrado acima (par exibido).'
            });
            return;
        }
        const lowPrice = Math.min(minP, maxP);
        const highPrice = Math.max(minP, maxP);
        const rawLo = displayPriceToRawTick(lowPrice);
        const rawHi = displayPriceToRawTick(highPrice);
        if (!Number.isFinite(rawLo) || !Number.isFinite(rawHi)) {
            setNotification({ type: 'error', text: 'Não foi possível converter preço em tick. Verifique os valores e o preço atual da pool.' });
            return;
        }
        const spacing = Number(pool.tickSpacing) || 1;
        let tickLower = alignTickToSpacing(Math.min(rawLo, rawHi), spacing, 'floor');
        let tickUpper = alignTickToSpacing(Math.max(rawLo, rawHi), spacing, 'ceil');
        if (tickLower >= tickUpper) {
            tickUpper = tickLower + spacing;
        }
        tickLower = Math.max(WHIRLPOOL_TICK_LOWER_BOUND, tickLower);
        tickUpper = Math.min(WHIRLPOOL_TICK_UPPER_BOUND, tickUpper);
        if (tickLower >= tickUpper) {
            setNotification({ type: 'error', text: 'Após alinhar ao tick spacing, o intervalo ficou inválido. Ajuste os preços.' });
            return;
        }
        quoteRequestRef.current += 1;
        setQuoteInfo(null);
        setForm(current => ({
            ...current,
            tickLowerIndex: tickLower,
            tickUpperIndex: tickUpper
        }));
        setNotification({
            type: 'success',
            text: `Ticks atualizados a partir do preço: ${tickLower} … ${tickUpper} (spacing ${spacing}).`
        });
    }

    function fillPriceRangeFromTicks() {
        if (!pool) return;
        setPriceRangeForm(priceRangeFormStateFromTicks(pool, form.tickLowerIndex, form.tickUpperIndex));
    }

    function getDisplayRangePrices() {
        if (!pool) return { min: '-', max: '-' };
        const lower = tickToDisplayPrice(form.tickLowerIndex);
        const upper = tickToDisplayPrice(form.tickUpperIndex);
        if (!lower || !upper) return { min: '-', max: '-' };
        return {
            min: formatNumber(Math.min(lower, upper), 8),
            max: formatNumber(Math.max(lower, upper), 8)
        };
    }

    function getDepositUsdEstimate() {
        if (form.totalUsd) return Number(form.totalUsd) || 0;
        const tokenAPrice = getTokenUsdPrice('A');
        const tokenBPrice = getTokenUsdPrice('B');
        return (Number(form.amountA || 0) * tokenAPrice) + (Number(form.amountB || 0) * tokenBPrice);
    }

    function getFeeEstimate24h() {
        const depositUsd = getDepositUsdEstimate();
        const fees24h = Number(pool?.fees24h || 0);
        if (!depositUsd || !fees24h) {
            return {
                depositUsd,
                share: 0,
                fees24h: 0,
                apr: 0
            };
        }

        const share = getEstimatedFeeShare(depositUsd);
        const estimatedFees = fees24h * share;
        return {
            depositUsd,
            share,
            fees24h: estimatedFees,
            apr: depositUsd ? (estimatedFees * 365 / depositUsd) * 100 : 0
        };
    }

    function getEstimatedFeeShare(depositUsd) {
        if (!isCurrentTickInsideSelectedRange()) return 0;

        const quoteLiquidity = quoteMatchesSelectedRange() ? Number(quoteInfo?.liquidityAmount || 0) : 0;
        const activePoolLiquidity = Number(pool?.liquidity || 0);
        if (Number.isFinite(quoteLiquidity) && quoteLiquidity > 0
            && Number.isFinite(activePoolLiquidity) && activePoolLiquidity > 0) {
            return quoteLiquidity / (activePoolLiquidity + quoteLiquidity);
        }

        const tvl = Number(pool?.tvlUsdc || 0);
        const concentratedDepositUsd = depositUsd * getSelectedRangeConcentrationMultiplier();
        return tvl ? concentratedDepositUsd / (tvl + concentratedDepositUsd) : 0;
    }

    function quoteMatchesSelectedRange() {
        if (!quoteInfo) return false;
        return Number(quoteInfo.tickLowerIndex) === Number(form.tickLowerIndex)
            && Number(quoteInfo.tickUpperIndex) === Number(form.tickUpperIndex);
    }

    function getSelectedRangeConcentrationMultiplier() {
        if (!pool) return 1;
        const selectedWidth = Math.abs(Number(form.tickUpperIndex) - Number(form.tickLowerIndex));
        if (!Number.isFinite(selectedWidth) || selectedWidth <= 0) return 1;

        const mediumRange = getRangeForPool(pool, 'medium');
        const mediumWidth = Math.abs(Number(mediumRange.upper) - Number(mediumRange.lower));
        if (!Number.isFinite(mediumWidth) || mediumWidth <= 0) return 1;

        return mediumWidth / selectedWidth;
    }

    function isCurrentTickInsideSelectedRange() {
        const currentTick = Number(pool?.tickCurrentIndex);
        const lower = Number(form.tickLowerIndex);
        const upper = Number(form.tickUpperIndex);
        if (!Number.isFinite(currentTick) || !Number.isFinite(lower) || !Number.isFinite(upper)) return false;
        return currentTick >= lower && currentTick < upper;
    }

    function displayToken(side) {
        return pool?.display?.[side] || pool?.[side];
    }

    function balanceForToken(token) {
        if (!pool || !token) return 0;
        const assetBalance = getBalanceForWalletToken(token, walletAssets);
        if (assetBalance > 0) return assetBalance;
        if (tokensMatch(token, displayToken('tokenA'))) return balances.tokenA;
        if (tokensMatch(token, displayToken('tokenB'))) return balances.tokenB;
        return 0;
    }

    function displayTokenFromPool(poolData, side) {
        return poolData?.display?.[side] || poolData?.[side];
    }

    function normalizedTokenMint(token) {
        const symbol = token?.symbol?.toUpperCase();
        if (symbol === 'ORCA') return ORCA_MINT;
        if (symbol === 'USDC') return USDC_MINT;
        return token?.mint;
    }

    function tokensMatch(left, right) {
        return normalizedTokenMint(left) === normalizedTokenMint(right);
    }

    function tokenLabel(token) {
        return token?.symbol || shortAddress(token?.mint);
    }

    function tokenIcon(token) {
        if (token?.imageUrl) return <img src={token.imageUrl} alt={tokenLabel(token)} />;
        return <span>{(tokenLabel(token) || '?').slice(0, 1)}</span>;
    }

    function positionFeeText() {
        if (!position) return '-';
        return `${formatNumber(position.pendingFees?.tokenA?.amount || 0, 8)} ${tokenLabel(position.pendingFees?.tokenA)} / ${formatNumber(position.pendingFees?.tokenB?.amount || 0, 8)} ${tokenLabel(position.pendingFees?.tokenB)}`;
    }

    function collectedFeeText() {
        if (!position) return '-';
        return `${formatNumber(position.collectedFees?.tokenA?.amount || 0, 8)} ${tokenLabel(position.collectedFees?.tokenA)} / ${formatNumber(position.collectedFees?.tokenB?.amount || 0, 8)} ${tokenLabel(position.collectedFees?.tokenB)}`;
    }

    function shortAddress(address) {
        if (!address) return '';
        return `${address.slice(0, 4)}...${address.slice(-4)}`;
    }

    function formatNumber(value, maximumFractionDigits = 8) {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return value || '-';
        return parsed.toLocaleString('pt-BR', { maximumFractionDigits });
    }

    function formatCurrency(value) {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return '$ -';
        return parsed.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
    }

    const displayRange = getDisplayRangePrices();
    const feeEstimate = getFeeEstimate24h();
    const positionUsdValue = getPositionUsdValue();
    const positionFeesUsdValue = getPositionPendingFeesUsdValue();
    const positionCollectedFeesUsdValue = getPositionCollectedFeesUsdValue();
    const positionFeeEstimates = getPositionFeeEstimatesByPeriod();
    const estimatedWithdraw = getEstimatedWithdrawAmounts();
    const depositLiquidityWarnings = buildLiquidityDepositWarnings({
        pool,
        tickLowerIndex: form.tickLowerIndex,
        tickUpperIndex: form.tickUpperIndex,
        positionAddress: position?.positionAddress || positionAddress || null
    });

    return (
        <>
            <main className="content orca-page orca-full-page">
                <div className="orca-container">
                    <section className="orca-hero liquidity-hero">
                        <div className="orca-hero-copy">
                            <span className="orca-eyebrow">Add liquidity</span>
                            <h1>{pool ? `${tokenLabel(displayToken('tokenA'))} / ${tokenLabel(displayToken('tokenB'))}` : 'Pool'}</h1>
                            <p>Escolha seu range, confira saldos e defina os valores para fornecer liquidez concentrada.</p>
                            <div className="orca-hero-actions">
                                <button className="orca-ghost-btn" onClick={() => navigate('/orca')}>Voltar para pools</button>
                                <button className="orca-ghost-btn" onClick={() => navigate(`/orca/swap?pool=${poolAddress}`)}>Swap</button>
                                <button className="orca-ghost-btn" onClick={() => navigate('/orca/portfolio')}>Meu portfólio</button>
                                <button className="orca-ghost-btn" onClick={() => navigate('/orca/positions')}>Minhas posições</button>
                            </div>
                        </div>
                        <div className="orca-wallet-panel">
                            <WalletMultiButton />
                            <div className="orca-wallet-status">
                                <strong>{connected ? 'Carteira conectada' : 'Conectar carteira'}</strong>
                                <span>{connected && publicKey ? shortAddress(publicKey.toBase58()) : 'Phantom, Solflare ou WalletConnect'}</span>
                                {wallet?.adapter?.name ? <small>{wallet.adapter.name}</small> : <small>Selecione sua carteira.</small>}
                            </div>
                        </div>
                    </section>

                    {pool && (
                        <section className="liquidity-layout">
                            <div className="orca-card glass">
                                <div className="orca-selected-pool big">
                                    <div className="orca-token-icons large">
                                        <div className="orca-token-icon">{tokenIcon(displayToken('tokenA'))}</div>
                                        <div className="orca-token-icon second">{tokenIcon(displayToken('tokenB'))}</div>
                                    </div>
                                    <div>
                                        <strong>{tokenLabel(displayToken('tokenA'))} / {tokenLabel(displayToken('tokenB'))}</strong>
                                        <small>{shortAddress(pool.address)} · fee {pool.feeRate} · tick {pool.tickSpacing}</small>
                                    </div>
                                </div>

                                <div className="liquidity-metrics">
                                    <div><small>{tokenLabel(displayToken('tokenB'))} por {tokenLabel(displayToken('tokenA'))}</small><strong>{formatNumber(pool.display?.price || pool.price, 8)}</strong></div>
                                    <div><small>TVL</small><strong>{formatCurrency(pool.tvlUsdc)}</strong></div>
                                    <div><small>Volume 24h</small><strong>{formatCurrency(pool.volume24h)}</strong></div>
                                </div>

                                <h3>Range de preço</h3>
                                <div className="orca-range-presets">
                                    {['narrow', 'medium', 'wide', 'full'].map(preset => (
                                        <button key={preset} className={rangePreset === preset ? 'active' : ''} onClick={() => onRangePresetChange(preset)}>
                                            {preset === 'narrow' ? 'Estreito' : preset === 'medium' ? 'Médio' : preset === 'wide' ? 'Amplo' : 'Full'}
                                        </button>
                                    ))}
                                </div>
                                <div className="orca-range-visual">
                                    <div className="orca-range-track"><span /></div>
                                    <div className="orca-range-values">
                                        <div><small>Min</small><strong>{displayRange.min}</strong></div>
                                        <div><small>Atual</small><strong>{formatNumber(pool.display?.price || pool.price, 8)}</strong></div>
                                        <div><small>Max</small><strong>{displayRange.max}</strong></div>
                                    </div>
                                </div>
                                <div className="orca-form-grid">
                                    <div className="orca-field">
                                        <label>Tick lower</label>
                                        <input className="form-control" name="tickLowerIndex" value={form.tickLowerIndex} onChange={updateField} />
                                    </div>
                                    <div className="orca-field">
                                        <label>Tick upper</label>
                                        <input className="form-control" name="tickUpperIndex" value={form.tickUpperIndex} onChange={updateField} />
                                    </div>
                                </div>
                                <p className="orca-muted balance-debug" style={{ marginTop: 6, marginBottom: 0 }}>
                                    O Whirlpool grava o range como índices inteiros de tick na rede; os preços (SOL por ORCA, etc.) são derivados para leitura humana.
                                    O resumo Min / Atual / Max acima e os campos de preço abaixo refletem o mesmo intervalo que estes ticks.
                                </p>
                                <div className="orca-field" style={{ marginTop: 12 }}>
                                    <label>Definir range por preço exibido ({tokenLabel(displayToken('tokenB'))} por {tokenLabel(displayToken('tokenA'))})</label>
                                    <p className="orca-muted balance-debug" style={{ marginTop: 0 }}>
                                        Os valores devem estar na mesma base do preço &quot;Atual&quot; acima. Use &quot;Preencher dos ticks&quot; e ajuste, ou digite os preços e clique em aplicar para gerar ticks alinhados ao spacing ({pool.tickSpacing}).
                                    </p>
                                    <div className="orca-form-grid">
                                        <div className="orca-field">
                                            <label>Preço mín</label>
                                            <input
                                                className="form-control"
                                                value={priceRangeForm.min}
                                                onChange={event => setPriceRangeForm(current => ({ ...current, min: event.target.value }))}
                                                placeholder={displayRange.min !== '-' ? displayRange.min : '0.0'}
                                                type="text"
                                                inputMode="decimal"
                                            />
                                        </div>
                                        <div className="orca-field">
                                            <label>Preço máx</label>
                                            <input
                                                className="form-control"
                                                value={priceRangeForm.max}
                                                onChange={event => setPriceRangeForm(current => ({ ...current, max: event.target.value }))}
                                                placeholder={displayRange.max !== '-' ? displayRange.max : '0.0'}
                                                type="text"
                                                inputMode="decimal"
                                            />
                                        </div>
                                    </div>
                                    <div className="orca-actions" style={{ marginTop: 8 }}>
                                        <button type="button" className="orca-ghost-btn" onClick={fillPriceRangeFromTicks}>Preencher dos ticks</button>
                                        <button type="button" className="orca-primary-btn" onClick={applyDisplayPricesToTicks}>Aplicar preços → ticks</button>
                                    </div>
                                </div>

                                {position && (
                                    <div className="managed-position-card">
                                        <div className="orca-section-header compact">
                                            <div>
                                                <h2>Sua posição</h2>
                                                <p>{shortAddress(position.positionAddress)} · {position.inRange ? 'dentro do range' : 'fora do range'}</p>
                                            </div>
                                            <button
                                                className="orca-ghost-btn"
                                                onClick={() => refreshPositionData(false)}
                                                disabled={loading === 'position'}
                                            >
                                                {loading === 'position' ? 'Atualizando...' : 'Atualizar dados'}
                                            </button>
                                        </div>
                                        <div className="position-grid">
                                            <div><small>Liquidez</small><strong>{formatNumber(position.liquidity, 0)}</strong></div>
                                            <div><small>Saldo na posição</small><strong>{formatNumber(position.tokenAmounts?.tokenA?.amount || 0, 6)} {tokenLabel(position.tokenAmounts?.tokenA)} / {formatNumber(position.tokenAmounts?.tokenB?.amount || 0, 6)} {tokenLabel(position.tokenAmounts?.tokenB)}</strong></div>
                                            <div>
                                                <small>Taxas disponíveis para sacar</small>
                                                <strong>{formatCurrency(positionFeesUsdValue)}</strong>
                                                <span className="orca-muted">{positionFeeText()}</span>
                                            </div>
                                            <div>
                                                <small>Histórico já sacado</small>
                                                <strong>{formatCurrency(positionCollectedFeesUsdValue)}</strong>
                                                <span className="orca-muted">{collectedFeeText()}</span>
                                                <span className="orca-muted">{position.collectedFees?.transactionCount || 0} coletas encontradas</span>
                                            </div>
                                            <div><small>Valor estimado</small><strong>{formatCurrency(positionUsdValue)}</strong></div>
                                        </div>
                                        <div className="liquidity-fee-estimate position-fee-periods">
                                            {positionFeeEstimates.map(period => (
                                                <div key={period.key}>
                                                    <small>Estimativa {period.label}</small>
                                                    <strong>{formatCurrency(period.amountUsd)}</strong>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="orca-field">
                                            <label>Valor para sacar em USD</label>
                                            <input
                                                className="form-control"
                                                name="removeUsd"
                                                value={form.removeUsd}
                                                onChange={updateField}
                                                placeholder={positionUsdValue ? positionUsdValue.toFixed(2) : '0.00'}
                                                type="number"
                                            />
                                        </div>
                                        <div className="liquidity-fee-estimate">
                                            <div>
                                                <small>Saque estimado</small>
                                                <strong>
                                                    {formatNumber(estimatedWithdraw.tokenA, 6)} {tokenLabel(position.tokenAmounts?.tokenA)}
                                                    {' / '}
                                                    {formatNumber(estimatedWithdraw.tokenB, 6)} {tokenLabel(position.tokenAmounts?.tokenB)}
                                                </strong>
                                            </div>
                                            <div>
                                                <small>Range da posição</small>
                                                <strong>{formatNumber(position.range?.min, 8)} - {formatNumber(position.range?.max, 8)}</strong>
                                            </div>
                                        </div>
                                        {hasPendingPositionFees() && (
                                            <p className="orca-muted small">
                                                Taxas pendentes: {positionFeeText()}. Ao sacar ou encerrar, o app coleta as taxas,
                                                aplica 1% para a plataforma (se houver valor) e continua com o saque.
                                            </p>
                                        )}
                                        <div className="orca-actions">
                                            <button
                                                className="orca-ghost-btn"
                                                onClick={() => setForm(current => ({ ...current, removeUsd: positionUsdValue ? positionUsdValue.toFixed(2) : '' }))}
                                            >
                                                MAX USD
                                            </button>
                                            <button className="orca-warning-btn" disabled={loading === 'remove'} onClick={() => onRemoveLiquidity(false)}>
                                                {loading === 'remove' ? 'Sacando...' : 'Sacar liquidez'}
                                            </button>
                                            <button className="orca-danger-btn" disabled={loading === 'close'} onClick={() => onRemoveLiquidity(true)}>
                                                {loading === 'close' ? 'Encerrando...' : 'Encerrar posição'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <aside className="orca-trade-card">
                                <div className="orca-section-header compact">
                                    <div>
                                        <h2>Depositar</h2>
                                        <p>{loading === 'balances' ? 'Atualizando saldos da carteira...' : 'Informe o valor total aproximado em USD.'}</p>
                                    </div>
                                    <button
                                        className="orca-ghost-btn"
                                        onClick={onRefreshBalances}
                                        disabled={!connected || !pool || loading === 'balances'}
                                    >
                                        {loading === 'balances' ? 'Atualizando...' : 'Atualizar saldo'}
                                    </button>
                                </div>
                                <div className="orca-field">
                                    <label>Total aproximado em USD</label>
                                    <p className="orca-muted balance-debug" style={{ marginTop: 0 }}>
                                        Após ~0,5s os montantes são calculados com várias cotações da pool até o valor em USD bater com o total (curva e range reais). Mantém «Calcular automaticamente o equilíbrio do par» ligado para refinar quando mudar ticks ou slippage.
                                    </p>
                                    <input className="form-control" name="totalUsd" value={form.totalUsd} onChange={onTotalUsdChange} placeholder="100.00" type="number" />
                                </div>

                                <div className="liquidity-token-input">
                                    <div className="orca-token-pair">
                                        <div className="orca-token-icon">{tokenIcon(displayToken('tokenA'))}</div>
                                        <div><strong>{tokenLabel(displayToken('tokenA'))}</strong><small>Saldo {formatNumber(balanceForToken(displayToken('tokenA')), 6)}</small></div>
                                    </div>
                                    <input name="amountA" value={form.amountA} onChange={updateField} placeholder="0.00" type="number" />
                                    <button onClick={() => onMaxAmount('A')}>MAX</button>
                                </div>

                                <div className="liquidity-token-input">
                                    <div className="orca-token-pair">
                                        <div className="orca-token-icon">{tokenIcon(displayToken('tokenB'))}</div>
                                        <div><strong>{tokenLabel(displayToken('tokenB'))}</strong><small>Saldo {formatNumber(balanceForToken(displayToken('tokenB')), 6)}</small></div>
                                    </div>
                                    <input name="amountB" value={form.amountB} onChange={updateField} placeholder="0.00" type="number" />
                                    <button onClick={() => onMaxAmount('B')}>MAX</button>
                                </div>

                                {balanceDebug && <p className="orca-muted balance-debug">{balanceDebug}</p>}
                                {quoteInfo && (
                                    <p className="orca-muted balance-debug">
                                        Estimativa on-chain: ~{formatInputAmount(quoteInfo.tokenEstA?.ui)} {tokenLabel(tokenMetaFromMint(quoteInfo.tokenA?.mint))}
                                        {' + '}
                                        ~{formatInputAmount(quoteInfo.tokenEstB?.ui)} {tokenLabel(tokenMetaFromMint(quoteInfo.tokenB?.mint))}
                                        <br />
                                        {formatDepositMaxHint(quoteInfo)}
                                    </p>
                                )}

                                <label className="orca-checkbox">
                                    <input type="checkbox" checked={autoSwap} onChange={event => setAutoSwap(event.target.checked)} />
                                    Calcular automaticamente o equilíbrio do par
                                </label>

                                <div className="liquidity-fee-estimate">
                                    <div>
                                        <small>Taxas estimadas em 24h</small>
                                        <strong>{formatCurrency(feeEstimate.fees24h)}</strong>
                                    </div>
                                    <div>
                                        <small>Participação estimada</small>
                                        <strong>{formatNumber(feeEstimate.share * 100, 6)}%</strong>
                                    </div>
                                    <div>
                                        <small>APR por fees</small>
                                        <strong>{formatNumber(feeEstimate.apr, 2)}%</strong>
                                    </div>
                                </div>

                                {depositLiquidityWarnings.length > 0 && (
                                    <div className="orca-muted balance-debug" style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, background: 'rgba(255, 193, 7, 0.12)', border: '1px solid rgba(255, 193, 7, 0.35)' }}>
                                        {depositLiquidityWarnings.map((w, i) => (
                                            <p key={i} style={{ margin: i ? '8px 0 0 0' : 0 }}>{w}</p>
                                        ))}
                                    </div>
                                )}

                                <div className="orca-field">
                                    <label>Slippage (bps)</label>
                                    <p className="orca-muted balance-debug" style={{ marginTop: 0 }}>
                                        {'100 bps = 1%. O servidor alarga o limite de preço on-chain (várias vezes o valor do campo). Antes de assinar «abrir posição» ou «aumentar liquidez», a cotação é renovada na rede. Pares voláteis: 2000–8000 bps (20–80%). Log: localStorage.setItem("orcaLiquidityDebug","1"). Servidor: ORCA_DEPOSIT_DEBUG=1.'}
                                    </p>
                                    <input className="form-control" name="slippageBps" value={form.slippageBps} onChange={updateField} type="number" min={1} max={10000} />
                                </div>

                                <button className="orca-primary-btn wide" disabled={loading === 'add'} onClick={onAddLiquidity}>
                                    {loading === 'add' ? 'Enviando...' : position ? 'Adicionar mais liquidez' : 'Adicionar liquidez'}
                                </button>
                                {operationStatus && (
                                    <div className="orca-operation-status">
                                        {operationStatus}
                                    </div>
                                )}
                            </aside>
                        </section>
                    )}
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default OrcaLiquidity;
